import type { Account, AccountNode, Entry, EntryKind, EntrySource, Posting } from '@taler/api-client'
import {
  absMoney,
  addMoney,
  type CurrencyCode,
  equalsMoney,
  type Locale,
  type LocalDate,
  type Money,
  type Rate,
  resolveLocalized,
  signOfMoney,
} from '@taler/domain'
import { flowMoney, moneyLeaves, signTone } from '@/app/accounting-view'

export type AccountIndex = ReadonlyMap<string, Account>

export function indexAccounts(nodes: readonly AccountNode[]): AccountIndex {
  const index = new Map<string, Account>()
  const walk = (level: readonly AccountNode[]): void => {
    for (const node of level) {
      index.set(node.account.id, node.account)
      walk(node.children)
    }
  }
  walk(nodes)
  return index
}

/**
 * Leaf name plus the ancestry that earns a place only in a `title` tooltip, and the account's
 * own user-chosen icon and colour - its identity, drawn as a small swatch beside the name so
 * "Wallet" is findable by eye, never as a colour on the text or the figure.
 */
export interface AccountLabel {
  readonly leaf: string
  readonly path: string
  /** Absent only for an account the index cannot resolve. */
  readonly icon?: string | undefined
  readonly color?: string | undefined
}

/**
 * The one leg of a cross-ledger FX transfer this ledger's own journal can see - the
 * counterparty leg is a separate entry in the other ledger's own journal (see
 * `packages/testing/src/server/handlers/transfers.ts`), so this row never claims to know
 * the other side's amount. `rateToBase` is `PostingResponse.rateToBase`, already applied
 * server-side; the row only ever displays it, never uses it to derive a figure.
 */
export interface FxLeg {
  readonly amount: Money
  readonly rateToBase: Rate | undefined
}

/**
 * An entry's structure in one line: the account the money left, the account or category it
 * reached, and how many more accounts the entry touches. An FX residual leg is left out unless
 * it is the only counterparty there is, so a foreign-currency entry reads as the pair a person
 * posted. `from` is absent only for an entry whose postings resolve to no known account.
 */
export interface TransactionSummary {
  readonly from: AccountLabel | undefined
  readonly to: AccountLabel | undefined
  readonly more: number
}

/** One kind's effect on the books for this entry, in one currency. Never converted. */
export interface TransactionEffect {
  readonly kind: Exclude<EntryKind, undefined>
  readonly amount: Money
}

export interface TransactionRow {
  readonly id: string
  readonly date: LocalDate
  readonly title: string
  readonly account: AccountLabel | undefined
  readonly summary: TransactionSummary
  readonly effects: readonly TransactionEffect[]
  readonly fx: FxLeg | undefined
  /** The display-currency figure when the server converted the entry, else the represented posting's own; undefined when neither exists. */
  readonly total: Money | undefined
  /** The represented posting's own-currency figure, present only when `total` is a conversion of it. */
  readonly nativeTotal: Money | undefined
  readonly displayRateMissing: boolean
  readonly kind: EntryKind | undefined
  readonly source: EntrySource
  readonly tags: readonly string[]
  readonly isReversal: boolean
  readonly isRevaluation: boolean
}

export type MoneyTone = 'transfer' | 'positive' | 'negative'

/** A transfer keeps its neutral tone; every other figure takes the tone of the sign it is shown with. */
export function entryTone(kind: EntryKind | undefined, amount: Money): MoneyTone | undefined {
  return kind === 'TRANSFER' ? 'transfer' : signTone(amount)
}

/** Plain-language sign: money out is negative, money in positive, whatever the ledger stores. */
function name(account: Account, locale: Locale): string {
  return resolveLocalized(account.names, locale)?.name ?? account.code
}

/** Walks `parentId` up to the root; the index already holds every ancestor. */
export function accountLabel(account: Account, index: AccountIndex, locale: Locale): AccountLabel {
  const chain: string[] = []
  const seen = new Set<string>()
  let current: Account | undefined = account
  while (current !== undefined && !seen.has(current.id)) {
    seen.add(current.id)
    chain.unshift(name(current, locale))
    current = current.parentId === undefined ? undefined : index.get(current.parentId)
  }
  return {
    leaf: chain[chain.length - 1] ?? account.code,
    path: chain.join(' / '),
    icon: account.icon,
    color: account.color,
  }
}

/** The single leg this ledger's journal holds for a cross-ledger FX transfer. */
function fxLeg(entry: Entry, accounts: AccountIndex): FxLeg | undefined {
  const posting = moneyPostingsOf(entry, accounts)[0]
  return posting === undefined ? undefined : { amount: posting.amount, rateToBase: posting.rateToBase }
}

/** The display-currency figure when it converts `native`; an own-currency figure is undefined and keeps every digit. */
export function convertedFigure(native: Money, display: Money | undefined): Money | undefined {
  return display === undefined || display.currency === native.currency ? undefined : display
}

/** The account class the money actually moved through: an ASSET or LIABILITY account. */
export function isMoneyClass(accountClass: Account['accountClass'] | undefined): boolean {
  return accountClass === 'ASSET' || accountClass === 'LIABILITY'
}

/**
 * The whole `EQUITY.FX_GAIN_LOSS` subtree, not only that code: a real chart posts residuals to
 * `.HISTORIC`, `.CONTACTS` and `.REVALUATION` children, and matching the parent alone left every
 * production residual reading as an ordinary posting. Keyed on the code rather than
 * `Account.isSystem`, which is true for real categories too.
 */
export function isResidualAccount(account: Account | undefined): boolean {
  return account !== undefined && (account.code === RESIDUAL_CODE || account.code.startsWith(RESIDUAL_PREFIX))
}

const RESIDUAL_CODE = 'EQUITY.FX_GAIN_LOSS'
const RESIDUAL_PREFIX = `${RESIDUAL_CODE}.`

export function moneyPostingsOf(entry: Entry, accounts: AccountIndex): readonly Posting[] {
  return entry.postings.filter((posting) => isMoneyClass(accounts.get(posting.accountId)?.accountClass))
}

function identifiedPostingOf(entry: Entry, money: readonly Posting[]): Posting | undefined {
  if (money.length === 1) return money[0]
  const total = absMoney(entry.total)
  const matching = entry.postings.filter((posting) => equalsMoney(absMoney(posting.amountBase), total))
  if (matching.length === 1) return matching[0]
  if (matching.length > 1) {
    const incoming = matching.filter((posting) => posting.amount.units > 0n)
    return incoming.length === 1 ? incoming[0] : undefined
  }
  return undefined
}

/** The posting an entry's figure stands for; with a missing display rate, only when all its money is in the display currency. */
export function representedPostingOf(
  entry: Entry,
  accounts: AccountIndex,
  displayCurrency?: CurrencyCode,
): Posting | undefined {
  const money = moneyPostingsOf(entry, accounts)
  if (!entry.displayRateMissing) return identifiedPostingOf(entry, money)
  const paid = money.filter((posting) => posting.amount.units < 0n)
  const posting = identifiedPostingOf(entry, money) ?? (paid.length === 1 ? paid[0] : undefined)
  const everyMoneyInDisplay = money.every((leg) => leg.amount.currency === displayCurrency)
  return everyMoneyInDisplay && posting?.amount.currency === displayCurrency ? posting : undefined
}

/** Recognised by the journal's adjustment kind, never by memo text. */
export function isRevaluation(entry: Entry): boolean {
  return entry.adjustmentKind === 'FX_REVALUATION'
}

/**
 * A valuation posting's change in the display currency. A posting already in that currency keeps its
 * native amount, which a valuation leaves at zero; any other reads the change the server converted.
 * Absent with a missing display rate.
 */
export function valuationFigureOf(entry: Entry, posting: Posting): Money | undefined {
  if (entry.displayRateMissing || entry.displayTotal === undefined) return undefined
  if (posting.amount.currency === entry.displayTotal.currency) return posting.amount
  return posting.displayAmount
}

/**
 * The valuation's net change in the display currency: the revalued accounts' own figures summed, the FX
 * gain or loss leg left out. Absent with a missing display rate or a figure the server did not value.
 */
export function revaluationTotalOf(entry: Entry, accounts: AccountIndex): Money | undefined {
  const figures = entry.postings
    .filter((posting) => !isResidualAccount(accounts.get(posting.accountId)))
    .map((posting) => valuationFigureOf(entry, posting))
  const present = figures.filter((figure): figure is Money => figure !== undefined)
  if (present.length === 0 || present.length < figures.length) return undefined
  return present.reduce(addMoney)
}

const EFFECT_ORDER: readonly Exclude<EntryKind, undefined>[] = ['INCOME', 'EXPENSE', 'TRANSFER']

/** Which way the money moved: the sign of the category legs' base amounts, a refund reading against its kind. */
export function categorySign(entry: Entry, accounts: AccountIndex): -1 | 0 | 1 {
  const units = entry.postings.reduce((sum, posting) => {
    const accountClass = accounts.get(posting.accountId)?.accountClass
    return accountClass === 'INCOME' || accountClass === 'EXPENSE' ? sum + posting.amountBase.units : sum
  }, 0n)
  return units > 0n ? 1 : units < 0n ? -1 : 0
}

/** Category postings signed plainly unless accounting view; a transfer effect is its magnitude. */
function effectsOf(entry: Entry, accounts: AccountIndex, accountingView: boolean): TransactionEffect[] {
  const sums = new Map<string, TransactionEffect>()
  const add = (kind: Exclude<EntryKind, undefined>, amount: Money): void => {
    const key = `${kind}:${amount.currency}`
    const existing = sums.get(key)
    sums.set(key, { kind, amount: existing === undefined ? amount : addMoney(existing.amount, amount) })
  }
  for (const posting of entry.postings) {
    const accountClass = accounts.get(posting.accountId)?.accountClass
    if (accountClass === 'INCOME' || accountClass === 'EXPENSE') add(accountClass, posting.amount)
    else if (entry.kind === 'TRANSFER' && isMoneyClass(accountClass) && posting.amount.units > 0n)
      add('TRANSFER', posting.amount)
  }
  return [...sums.values()]
    .filter((effect) => effect.amount.units !== 0n)
    .map((effect) => ({
      kind: effect.kind,
      amount: accountingView
        ? effect.amount
        : flowMoney(effect.kind, effect.amount, signOfMoney(effect.amount)),
    }))
    .sort((a, b) => EFFECT_ORDER.indexOf(a.kind) - EFFECT_ORDER.indexOf(b.kind))
}

/**
 * One account the entry touches, with its postings' base amounts summed so two legs on the same
 * account read as one side. `amountBase` is the ledger's own base currency on every posting, so
 * its units order sides that carry different native currencies against each other.
 */
interface StructuralSide {
  readonly account: Account
  readonly base: bigint
  readonly isResidual: boolean
  readonly isCategory: boolean
  readonly isMoney: boolean
}

function structuralSides(entry: Entry, accounts: AccountIndex): readonly StructuralSide[] {
  const sums = new Map<string, { readonly account: Account; base: bigint }>()
  for (const posting of entry.postings) {
    const account = accounts.get(posting.accountId)
    if (account === undefined) continue
    const existing = sums.get(account.id)
    if (existing === undefined) sums.set(account.id, { account, base: posting.amountBase.units })
    else existing.base += posting.amountBase.units
  }
  const sides = [...sums.values()].map(({ account, base }) => ({
    account,
    base,
    isResidual: isResidualAccount(account),
    isCategory: account.accountClass === 'EXPENSE' || account.accountClass === 'INCOME',
    isMoney: isMoneyClass(account.accountClass),
  }))
  const posted = sides.filter((side) => !side.isResidual)
  return posted.length >= 2 ? posted : sides
}

/**
 * Which side leads when the base amounts tie: income arrives from its category into an account,
 * everything else leaves an account for one. Posting order only decides between sides of the same
 * kind of account.
 */
function leadingSide(kind: EntryKind | undefined): (side: StructuralSide) => boolean {
  return kind === 'INCOME' ? (side) => side.isCategory : (side) => side.isMoney
}

/** Money leaves the first side, so the most negative one wins. */
function compareFrom(a: StructuralSide, b: StructuralSide, kind: EntryKind | undefined): number {
  if (a.isResidual !== b.isResidual) return a.isResidual ? 1 : -1
  if (a.base !== b.base) return a.base < b.base ? -1 : 1
  const leads = leadingSide(kind)
  if (leads(a) !== leads(b)) return leads(a) ? -1 : 1
  return 0
}

/** Where the money landed: the most positive side left. */
function compareTo(a: StructuralSide, b: StructuralSide, kind: EntryKind | undefined): number {
  if (a.isResidual !== b.isResidual) return a.isResidual ? 1 : -1
  if (a.base !== b.base) return a.base > b.base ? -1 : 1
  const leads = leadingSide(kind)
  if (leads(a) !== leads(b)) return leads(a) ? 1 : -1
  return 0
}

function best<T>(items: readonly T[], compare: (a: T, b: T) => number): T | undefined {
  return items.reduce<T | undefined>(
    (winner, item) => (winner === undefined || compare(item, winner) < 0 ? item : winner),
    undefined,
  )
}

/**
 * The money account the entry moved through: when several money legs exist, the largest one in the
 * direction the money went - the largest outflow for an expense or a transfer's source, the largest
 * inflow for an income or a refund.
 */
function moneyAccountOf(entry: Entry, accounts: AccountIndex, locale: Locale): AccountLabel | undefined {
  const moneyLeft = moneyLeaves(entry.kind, categorySign(entry, accounts))
  const side = best(
    structuralSides(entry, accounts).filter((candidate) => candidate.isMoney),
    (a, b) => (moneyLeft ? compareFrom(a, b, entry.kind) : compareTo(a, b, entry.kind)),
  )
  return side === undefined ? undefined : accountLabel(side.account, accounts, locale)
}

function summaryOf(entry: Entry, accounts: AccountIndex, locale: Locale): TransactionSummary {
  const sides = structuralSides(entry, accounts)
  const from = best(sides, (a, b) => compareFrom(a, b, entry.kind))
  const to = best(
    sides.filter((side) => side !== from),
    (a, b) => compareTo(a, b, entry.kind),
  )
  return {
    from: from === undefined ? undefined : accountLabel(from.account, accounts, locale),
    to: to === undefined ? undefined : accountLabel(to.account, accounts, locale),
    more: sides.length > 2 ? sides.length - 2 : 0,
  }
}

export function toTransactionRow(
  entry: Entry,
  accounts: AccountIndex,
  locale: Locale,
  accountingView = false,
  displayCurrency?: CurrencyCode,
): TransactionRow {
  const represented = representedPostingOf(entry, accounts, displayCurrency)
  const native = represented?.amount
  const converted = native === undefined ? undefined : convertedFigure(native, entry.displayTotal)
  // The ledger base currency is only a conversion step, so it is never the second line.
  const nativeIsBase = native?.currency === represented?.amountBase.currency
  const main = entry.displayTotal ?? native

  const direction = categorySign(entry, accounts)
  const signed = (money: Money): Money => (accountingView ? money : flowMoney(entry.kind, money, direction))
  const revaluation = isRevaluation(entry)
  return {
    id: entry.id,
    date: entry.date,
    title: entry.payee ?? entry.memo,
    account: revaluation ? undefined : moneyAccountOf(entry, accounts, locale),
    summary: revaluation ? { from: undefined, to: undefined, more: 0 } : summaryOf(entry, accounts, locale),
    effects: effectsOf(entry, accounts, accountingView),
    fx: entry.entryClass === 'FX_TRANSFER' ? fxLeg(entry, accounts) : undefined,
    total: revaluation ? revaluationTotalOf(entry, accounts) : main === undefined ? undefined : signed(main),
    nativeTotal:
      revaluation || native === undefined || converted === undefined || nativeIsBase
        ? undefined
        : signed(native),
    displayRateMissing: entry.displayRateMissing,
    kind: entry.kind,
    source: entry.source,
    tags: entry.tags,
    isReversal: entry.reversalOfEntryId !== undefined,
    isRevaluation: revaluation,
  }
}
